// =====================================
// Feature Manager (Centralized Control)
// =====================================

import { hasRight } from './rightsUtil.js';
import { resetActiveCommandsOnly } from './resetApplication.js';
import { updateBaseLegendMessage, updateLayerVisibilityByZoom } from './mapUtils.js';
import { getMap } from './mapInstance.js';
import { registerDock, setDockVisible } from './panelDockManager.js';
import { bringFloatingDialogToFront, placeFloatingDialog } from './dialogs.js';

let vertexEditToolbarCancelInitialized = false;
const FLOATING_PANEL_SELECTOR = [
  '#draw-panel',
  '#pc-panel',
  '#search-form',
  '#layer-info-panel',
  '#bookmark-panel',
  '#upload-panel',
  '#mapping-modal',
  '#draw-summary-panel',
  '#pc-summary-panel',
  '#floating-table-panel',
  '.sevenn-dialog'
].join(',');

function isPanelVisible(panel) {
  if (!panel) return false;
  const style = getComputedStyle(panel);
  return style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    Number(style.opacity || 1) !== 0 &&
    panel.getBoundingClientRect().width > 0 &&
    panel.getBoundingClientRect().height > 0;
}

function getTopFloatingPanelAtPoint(x, y) {
  return document.elementsFromPoint(x, y)
    .map(element => element.closest?.(FLOATING_PANEL_SELECTOR))
    .find(element => element && isPanelVisible(element)) || null;
}

function hasFloatingPanelAbove(panel) {
  if (!isPanelVisible(panel)) return false;

  const rect = panel.getBoundingClientRect();
  const points = [
    [rect.left + rect.width / 2, rect.top + rect.height / 2],
    [rect.left + Math.min(24, rect.width / 2), rect.top + Math.min(24, rect.height / 2)],
    [rect.right - Math.min(24, rect.width / 2), rect.top + Math.min(24, rect.height / 2)],
    [rect.left + Math.min(24, rect.width / 2), rect.bottom - Math.min(24, rect.height / 2)],
    [rect.right - Math.min(24, rect.width / 2), rect.bottom - Math.min(24, rect.height / 2)]
  ];

  return points.some(([x, y]) => {
    const topPanel = getTopFloatingPanelAtPoint(x, y);
    return topPanel && topPanel !== panel && panel.contains(document.elementFromPoint(x, y)) === false;
  });
}

function isFrontMostPanel(panel) {
  if (!isPanelVisible(panel)) return false;
  if (hasFloatingPanelAbove(panel)) return false;

  const panelZIndex = Number.parseInt(getComputedStyle(panel).zIndex, 10) || 0;
  const visiblePanels = Array.from(document.querySelectorAll(FLOATING_PANEL_SELECTOR))
    .filter(element => element !== panel && isPanelVisible(element));

  return !visiblePanels.some(element => {
    const zIndex = Number.parseInt(getComputedStyle(element).zIndex, 10) || 0;
    const elementFollowsPanel = Boolean(
      panel.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING
    );

    return zIndex > panelZIndex || (zIndex === panelZIndex && elementFollowsPanel);
  });
}

function toggleOrFocusPanel(panel, { show, hide, onShow } = {}) {
  if (!panel) return;

  if (!isPanelVisible(panel) || panel.__sevennGetMinimized?.()) {
    show?.();
    onShow?.();
    return;
  }

  if (hasFloatingPanelAbove(panel)) {
    bringFloatingDialogToFront(panel);
    return;
  }

  if (isFrontMostPanel(panel)) {
    hide?.();
    return;
  }

  bringFloatingDialogToFront(panel);
}

function setupDockablePanel(panel, {
  dockId,
  dockTop,
  minimizeBtnId,
  closeBtnId,
  dockTitle,
  restoreTitle,
  sidebarSelector,
}) {
  if (!panel) return;
  if (panel.parentElement !== document.body) {
    document.body.appendChild(panel);
  }
  if (panel.__sevennDockSetup) return;
  panel.__sevennDockSetup = true;

  const state = {
    minimized: false,
    storedGeometry: null,
    dockButton: null,
  };

  const captureGeometry = () => ({
    left: panel.style.left || '',
    right: panel.style.right || '',
    top: panel.style.top || '',
    bottom: panel.style.bottom || '',
    width: panel.style.width || '',
    height: panel.style.height || '',
    transform: panel.style.transform || '',
    zIndex: panel.style.zIndex || '',
  });

  const applyGeometry = (geometry) => {
    if (!geometry) return;
    if (geometry.left) panel.style.setProperty('left', geometry.left, 'important');
    else panel.style.removeProperty('left');
    if (geometry.right) panel.style.setProperty('right', geometry.right, 'important');
    else panel.style.removeProperty('right');
    if (geometry.top) panel.style.setProperty('top', geometry.top, 'important');
    else panel.style.removeProperty('top');
    if (geometry.bottom) panel.style.setProperty('bottom', geometry.bottom, 'important');
    else panel.style.removeProperty('bottom');
    if (geometry.width) panel.style.setProperty('width', geometry.width, 'important');
    else panel.style.removeProperty('width');
    if (geometry.height) panel.style.setProperty('height', geometry.height, 'important');
    else panel.style.removeProperty('height');
    if (geometry.transform) panel.style.setProperty('transform', geometry.transform, 'important');
    else panel.style.removeProperty('transform');
    if (geometry.zIndex) panel.style.setProperty('z-index', geometry.zIndex, 'important');
    else panel.style.removeProperty('z-index');
  };

  const ensureDockButton = () => {
    if (state.dockButton) return state.dockButton;

    const dockButton = document.createElement('button');
    dockButton.type = 'button';
    dockButton.className = 'btn btn-outline-secondary btn-sm panel-dock-tab';
    dockButton.id = dockId;
    dockButton.innerHTML = '<i class="bi bi-chevron-left"></i>';
    dockButton.title = restoreTitle;
    dockButton.setAttribute('aria-label', restoreTitle);
    dockButton.style.display = 'none';
    dockButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      panel.__sevennSetVisible?.(true);
    });
    document.body.appendChild(dockButton);
    registerDock(dockId, dockButton, dockTop, sidebarSelector);
    state.dockButton = dockButton;
    return dockButton;
  };

  const applyVisibleState = () => {
    panel.style.display = state.minimized ? 'none' : 'block';
    panel.classList.toggle('is-minimized', state.minimized);
    setDockVisible(dockId, state.minimized);
  };

  const showPanel = () => {
    if (state.minimized && state.storedGeometry) {
      applyGeometry(state.storedGeometry);
    }
    state.minimized = false;
    applyVisibleState();
  };

  const minimizePanel = () => {
    state.storedGeometry = captureGeometry();
    ensureDockButton();
    state.minimized = true;
    applyVisibleState();
  };

  const hidePanel = () => {
    state.minimized = false;
    panel.style.display = 'none';
    panel.classList.remove('is-minimized');
    setDockVisible(dockId, false);
    return;
  };

  const restorePanel = () => {
    if (state.minimized && state.storedGeometry) {
      applyGeometry(state.storedGeometry);
    }
    state.minimized = false;
    panel.style.display = 'block';
    panel.classList.remove('is-minimized');
    setDockVisible(dockId, false);
    requestAnimationFrame(() => placeFloatingDialog(panel));
  };

  const minimizeAndDockPanel = () => {
    state.storedGeometry = captureGeometry();
    ensureDockButton();
    state.minimized = true;
    applyVisibleState();
  };

  panel.__sevennSetVisible = (visible) => {
    if (visible) {
      restorePanel();
      return;
    }
    hidePanel();
  };

  panel.__sevennSetMinimized = (minimized) => {
    if (minimized) {
      minimizeAndDockPanel();
      return;
    }
    restorePanel();
  };

  panel.__sevennGetMinimized = () => state.minimized;
  panel.__sevennDockButton = ensureDockButton();

  const minimizeBtn = document.getElementById(minimizeBtnId);
  if (minimizeBtn && !minimizeBtn.__sevennDockBound) {
    minimizeBtn.__sevennDockBound = true;
    minimizeBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      panel.__sevennSetMinimized?.(true);
    });
  }

  const closeBtn = document.getElementById(closeBtnId);
  if (closeBtn && !closeBtn.__sevennDockBound) {
    closeBtn.__sevennDockBound = true;
    closeBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      panel.__sevennSetVisible?.(false);
    });
  }
}

function initVertexEditToolbarCancel() {
  if (vertexEditToolbarCancelInitialized) return;
  vertexEditToolbarCancelInitialized = true;

  const sidebar = document.getElementById('sidebar');
  if (!sidebar) return;

  sidebar.addEventListener('click', (event) => {
    const button = event.target.closest('button');
    if (!button || !sidebar.contains(button)) return;

    const keepVertexEdit = button.matches(
      '.zoom-box-button, .draw-panel-toggle, .pc-panel-toggle'
    );

    if (keepVertexEdit) return;

    document.dispatchEvent(new CustomEvent('draw-summary-edit-cancel'));
  }, true);
}

// Registry of all features
const FEATURES = [
{
  name: "RESET",
  right: "app.reset",
  init: () => {
    import('./resetApplication.js').then(mod => {
      document.getElementById("reset-app-button")
        ?.addEventListener("click", mod.resetApplication);
    });
  },
  elements: ["reset-app-button"]
},

{
  name: "SEARCH",
  right: "app.locationSearch",
  init: () => {

    if (!hasRight("app.locationSearch")) {
      console.warn("Search blocked by rights");
      return;
    }

    import('./searchByLocation.js').then(mod => {
      mod.setupSearchByLocation();
      mod.initSearchByLocationLayer();
    });
  }
},

  // -----------------------------
  // QUERY
  // -----------------------------
{
  name: "QUERY",
  right: "app.query",
  init: () => {
    import('./addressSearch.js').then(mod => {
      const form = document.getElementById('search-form');
      setupDockablePanel(form, {
        dockId: 'search-form-dock',
        dockTop: 396,
        minimizeBtnId: 'search-form-minimize-btn',
        closeBtnId: 'search-form-close',
        dockTitle: 'Restore address search',
        restoreTitle: 'Restore address search panel',
        sidebarSelector: '.query-button',
      });

      document.querySelector('.query-button')
        ?.addEventListener('click', () => {
          toggleOrFocusPanel(form, {
            show: () => {
              form.__sevennSetVisible?.(true);
              requestAnimationFrame(() => placeFloatingDialog(form));
            },
            hide: () => form.__sevennSetVisible?.(false)
          });
        });
    });
  },
  elements: ["query-button"]
},

  // -----------------------------
  // INFO TOOL
  // -----------------------------
  {
    name: "INFO",
    right: "map.info",
    init: () => {
      import('./featureinfo.js').then(mod => {
        mod.observeInfoButtonAndInit();
      });
    },
    elements: ["infoBtn"]
  },

  // -----------------------------
  // UPLOAD
  // -----------------------------
  {
    name: "UPLOAD",
    right: "data.upload",
    init: () => {
      import('./uploadModule.js').then(mod => {
        const sidebar = document.getElementById('sidebar');
        const panel = mod.setupUploadModule(sidebar);

        document.querySelector('.upload-button')
          ?.addEventListener('click', () => {
            toggleOrFocusPanel(panel, {
              show: () => {
                panel.classList.remove('d-none');
                requestAnimationFrame(() => placeFloatingDialog(panel));
              },
              hide: () => panel.classList.add('d-none')
            });
          });
      });
    },
    elements: ["uploadBtn"]
  },

  // -----------------------------
  // BOOKMARKS
  // -----------------------------
    {
    name: "BOOKMARKS",
    right: "app.bookmarks",
    init: () => {
        import('./bookmarks.js').then(mod => {
        const panel = mod.setupBookmarkForm();

        document.querySelector('.bookmark-button')
            ?.addEventListener('click', () => {
            toggleOrFocusPanel(panel, {
                show: () => panel.__sevennSetVisible?.(true),
                hide: () => panel.__sevennSetVisible?.(false)
            });
            });
        });
    }
    },

  // -----------------------------
  // DRAW TOOLS
  // -----------------------------
    {
    name: "DRAW",
    right: "data.Addresselection",
    init: () => {
        import('./drawTools.js').then(draw => {
        draw.setupDrawTools();
        });

        import('./addressSearch.js').then(addr => {
        addr.initDrawLayer();
        });
    }
    },

  // -----------------------------
  // LAYERS
  // -----------------------------
{
  name: "LAYERS",
  right: ["layers.base", "layers.overlay", "layers.own"],
  condition: (hasRight) =>
    hasRight("layers.base") ||
    hasRight("layers.overlay") ||
    hasRight("layers.own"),

  init: () => {
    import('./layerInfo.js').then(mod => {
      mod.populateLayerPanel();

      // SHOW PANEL
      const panel = document.getElementById("layer-info-panel");
      if (panel) {
        if (typeof panel.__sevennSetVisible === 'function') {
          panel.__sevennSetVisible(true);
        } else {
          panel.classList.remove("d-none");
          requestAnimationFrame(() => placeFloatingDialog(panel));
        }
      }

      // OPTIONAL: auto-open groups
      setTimeout(() => {
        toggleLayerGroups();
      }, 0);
    });
  }
},

  // -----------------------------
  // LANGUAGE
  // -----------------------------
    {
    name: "LANGUAGE",
    right: "app.languageToggle",
    init: (context) => {
        import('./country-language.js').then(mod => {
        mod.setupCountryLanguageSelectors(context.map);
        });
    }
    },

    {
  name: "FIT_BASE",
  right: "map.FitBaselayer",
  init: (context) => {
    import('./loadBaseLayers.js').then(mod => {

      document.querySelector('.fit-base-button')
        ?.addEventListener('click', () => {
          resetActiveCommandsOnly();
          mod.fitBaseLayers(context.map);

          setTimeout(() => {
            updateLayerVisibilityByZoom(window.base_map);
            updateBaseLegendMessage(window.base_map);
          }, 550);
        });

    });
  },
  elements: ["fit-base-button"]
},

{
  name: "PC_SELECTION",
  right: "data.pcSelection",
  init: () => {
    import('./pcSelection.js').then(mod => {
      const panel = document.getElementById('pc-panel');
      setupDockablePanel(panel, {
        dockId: 'pc-panel-dock',
        dockTop: 324,
        minimizeBtnId: 'pc-panel-minimize-btn',
        closeBtnId: 'pc-panel-close',
        dockTitle: "Restore Area Selections",
        restoreTitle: "Restore Area Selections panel",
        sidebarSelector: '.pc-panel-toggle',
      });

      // Toggle panel
        const btn = document.querySelector('.pc-panel-toggle');

        btn?.addEventListener('click', () => {
        toggleOrFocusPanel(panel, {
          show: () => panel.__sevennSetVisible?.(true),
          hide: () => panel.__sevennSetVisible?.(false),
          onShow: () => requestAnimationFrame(() => mod.updatePCSelectionButtons(getMap()))
        });
        });

      // PC buttons
      document.querySelector('.pc4-select')
        ?.addEventListener('click', () => mod.activatePCSelection('PC4'));

      document.querySelector('.pc5-select')
        ?.addEventListener('click', () => mod.activatePCSelection('PC5'));

      document.querySelector('.pc6-select')
        ?.addEventListener('click', () => mod.activatePCSelection('PC6'));

      document.querySelector('.pc-polygon')
        ?.addEventListener('click', mod.drawPCPolygon);

      document.querySelector('.pc-circle')
        ?.addEventListener('click', mod.drawPCCircle);

      document.querySelector('.pc-clear-selection')
        ?.addEventListener('click', mod.clearPCSelection);

      document.querySelector('.pc-current-results')
        ?.addEventListener('click', mod.loadPCDrawFromBackend);

    });
    
  },
  elements: ["pc-panel-toggle"]
},

{
  name: "DRAW_PANEL",
  right: "data.Addresselection",
  init: () => {
    const panel = document.getElementById('draw-panel');
    setupDockablePanel(panel, {
      dockId: 'draw-panel-dock',
      dockTop: 252,
      minimizeBtnId: 'draw-panel-minimize-btn',
      closeBtnId: 'draw-panel-close',
      dockTitle: 'Restore address selection',
      restoreTitle: 'Restore address selection panel',
      sidebarSelector: '.draw-panel-toggle',
    });

    const btn = document.querySelector('.draw-panel-toggle');

    btn?.addEventListener('click', () => {
      toggleOrFocusPanel(panel, {
        show: () => panel.__sevennSetVisible?.(true),
        hide: () => panel.__sevennSetVisible?.(false)
      });
    });
  }
},

  // -----------------------------
  // PAN
  // -----------------------------

    {
    name: "PAN",
    right: "navigate.pan",
    init: () => {
        import('./panUtils.js').then(mod => {
        document.querySelector('.pan-button')
            ?.addEventListener('click', mod.activatePanMode);
        });
    }
    },

    {
    name: "ZOOM",
    right: "map.ZoomWindow",
    init: (context) => {
        import('./Magnify.js').then(mod => {
        document.querySelector('.zoom-box-button')
            ?.addEventListener('click', () => mod.toggleDragZoom(context.map));
        });
    }
    }



];


// =====================================
// Initialize features based on rights
// =====================================
export function initFeaturesByRights(context = {}) {
  initVertexEditToolbarCancel();

  FEATURES.forEach(feature => {

    let allowed = false;

    // Case 1: custom condition
    if (feature.condition) {
      allowed = feature.condition(hasRight);
    }
    // Case 2: multiple rights
    else if (Array.isArray(feature.right)) {
      allowed = feature.right.some(r => hasRight(r));
    }
    // Case 3: single right
    else {
      allowed = hasRight(feature.right);
    }

    if (allowed) {
      feature.init(context);
    } else {
      hideElements(feature.elements);
    }

  });

}


// =====================================
// Hide UI elements
// =====================================
function hideElements(selectors = []) {
  selectors.forEach(selector => {
    document.querySelectorAll(`#${selector}, .${selector}`)
      .forEach(el => {
        el.style.display = "none";
      });
  });
}

// =====================================
// Layer group management
// =====================================
function toggleLayerGroups() {

  const groups = [
    { id: "base-layers", right: "layers.base", defaultOpen: true, group: () => window.base_map },
    { id: "overlay-layers", right: "layers.overlay", defaultOpen: false, group: () => window.overlays },
    { id: "own-layers", right: "layers.own", defaultOpen: false, group: () => window.own_map }
  ];

  groups.forEach(g => {

    const ul = document.getElementById(g.id);
    const groupDiv = ul?.closest('.layer-group');
    const btn = groupDiv?.querySelector('.group-toggle');

    if (!ul || !groupDiv || !btn) return;

    const layerGroup = g.group?.();
    const hasAllowedSubnodes = !!layerGroup?.getLayers?.()
      && layerGroup.getLayers().getArray().some(layer =>
        layer.get?.('title') &&
        !layer.get?.('disabledByRights')
      );

    if (hasRight(g.right) && hasAllowedSubnodes) {
      groupDiv.style.display = "";           // ensure visible
      groupDiv.style.opacity = "";
      groupDiv.style.pointerEvents = "";
      ul.classList.toggle("d-none", !g.defaultOpen);
      btn.style.display = "";                // ensure header visible

      const groupLabel = btn.textContent.replace(/^\S+\s*/, '');
      btn.textContent = (g.defaultOpen ? '\u25BC ' : '\u25B6 ') + groupLabel;

    } else {
      groupDiv.style.display = "none";
    }

  });
}
